import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ScheduleItem, ScheduleItemStatus } from "@/types/schedule";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import { getTaskAssigneeLabel } from "@/lib/taskAssigneeLabel";

function isBlockedReservation(r: { reservation_type?: string; summary?: string | null }): boolean {
  return (
    r.reservation_type === "blocked" ||
    (!!r.summary && r.summary.toLowerCase().includes("not available"))
  );
}

function buildItemsForProperty(
  reservations: Array<Record<string, unknown>>,
  cleaningTasks: CleaningTaskWithRelations[],
  ownerId?: string | null,
  assigneeUserIdToName?: Map<string, string>
): ScheduleItem[] {
  const list: ScheduleItem[] = [];
  const reservationIdsWithTask = new Set(
    cleaningTasks.map((t) => t.reservation_id).filter(Boolean)
  );

  reservations.forEach((r) => {
    const id = r.id as string;
    const startDate = r.start_date as string;
    const endDate = r.end_date as string;
    const blocked = isBlockedReservation(r as { reservation_type?: string; summary?: string | null });
    const hasTask = reservationIdsWithTask.has(id);
    const status: ScheduleItemStatus = blocked ? "scheduled" : hasTask ? "scheduled" : "no_task";

    const start = startDate.includes("T") ? startDate : `${startDate}T00:00:00`;
    const end = endDate.includes("T") ? endDate : `${endDate}T23:59:59`;

    const sourceLabel = blocked
      ? null
      : (r.is_manual ? "Manual" : (r.source as string) || null);
    const title = blocked ? (r.summary as string) || "Blocked" : "Reserved";
    list.push({
      id: `res-${id}`,
      type: "reservation",
      start,
      end,
      title,
      status,
      source: (r.source as string) || null,
      sourceLabel: sourceLabel || null,
      relatedReservationId: null,
      reservationPayload: r,
      isBlocked: blocked,
    });
    if (!blocked && !hasTask) {
      list.push({
        id: `need-${id}`,
        type: "turnover_needed",
        start: endDate,
        end: endDate,
        title: "Turnover needed",
        status: "no_task",
        reservationPayload: r,
        sourceLabel: sourceLabel || null,
      });
    }
  });

  cleaningTasks.forEach((t) => {
    const dateStr = t.scheduled_date;
    const hasTime = t.scheduled_start_at && t.scheduled_end_at;
    const start = hasTime ? t.scheduled_start_at : (dateStr.includes("T") ? dateStr : `${dateStr}T09:00:00`);
    const end = hasTime ? t.scheduled_end_at : (dateStr.includes("T") ? dateStr : `${dateStr}T10:00:00`);
    const status = (t.status as ScheduleItemStatus) || "scheduled";
    const checklistName = (t.checklists as { name?: string } | null)?.name ?? null;
    const res = t.reservations as { check_out_time?: string | null; check_in_time?: string | null } | null;
    const outTime = (t as { override_check_out_time?: string | null }).override_check_out_time ?? res?.check_out_time ?? "11:00 AM";
    const inTime = (t as { override_check_in_time?: string | null }).override_check_in_time ?? res?.check_in_time ?? "4:00 PM";
    const timeRangeDisplay = `${outTime} → ${inTime}`;

    const title =
      t.reservation_id
        ? "Turnover"
        : ((t as { notes?: string | null }).notes || (t.checklists as { name?: string } | null)?.name || "Cleaning");

    const raw = t as {
      cleaners?: { id: string; name: string } | { id: string; name: string }[] | null;
      cleaner?: { id: string; name: string } | null;
      assignee_user_id?: string | null;
      assigned_type?: "owner" | "cleaner" | "unassigned" | "team";
    };
    const cleanerName = (Array.isArray(raw.cleaners) ? raw.cleaners[0]?.name : raw.cleaners?.name) ?? raw.cleaner?.name ?? null;
    const maps = {
      getCleanerName: (id: string | null) => (id === t.cleaner_id ? cleanerName : null),
      getAssigneeUserName: (id: string | null) => (id ? (assigneeUserIdToName?.get(id) ?? null) : null),
    };
    const assignedToName = getTaskAssigneeLabel(raw, maps);
    list.push({
      id: `task-${t.id}`,
      type: "task",
      start,
      end,
      title,
      status,
      assignedTo: t.cleaner_id || null,
      assignedToName,
      relatedReservationId: t.reservation_id || null,
      taskSubtype: t.reservation_id ? "turnover" : "manual",
      taskPayload: t,
      checklistName: checklistName || null,
      timeRangeDisplay,
    });
  });

  list.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  return list;
}

export function useMultiPropertyScheduleItems(propertyIds: string[]) {
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const propertiesQuery = useQuery({
    queryKey: ["properties-owner-ids", propertyIds],
    queryFn: async () => {
      if (propertyIds.length === 0) return [] as Array<{ id: string; owner_id: string }>;
      const { data, error } = await supabase
        .from("properties")
        .select("id, owner_id")
        .in("id", propertyIds);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; owner_id: string }>;
    },
    enabled: propertyIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  const reservationQueries = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ["reservations", propertyId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("reservations")
          .select("*, early_checkout_detected")
          .eq("property_id", propertyId)
          .gte("end_date", today)
          .order("start_date", { ascending: true });
        if (error) throw error;
        return (data ?? []) as Array<Record<string, unknown>>;
      },
      enabled: !!propertyId,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    })),
  });

  const taskQueries = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ["cleaning-tasks", propertyId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("cleaning_tasks")
          .select("id, property_id, reservation_id, cleaner_id, assignee_user_id, coordinator_user_id, checklist_id, status, scheduled_date, scheduled_start_at, scheduled_end_at, override_check_in_time, override_check_out_time, assigned_type, assignment_type, assignment_reason, notes, created_at, completed_at, duration_minutes, cleaning_fee, checklists(id, name), cleaners(id, name)")
          .eq("property_id", propertyId)
          .order("scheduled_date", { ascending: true });
        if (error) {
          if (error.code === "400") return [];
          throw error;
        }
        return (data ?? []) as CleaningTaskWithRelations[];
      },
      enabled: !!propertyId,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    })),
  });

  const vendorVisitsQuery = useQuery({
    queryKey: ["vendor-visits", propertyIds],
    queryFn: async () => {
      if (propertyIds.length === 0) return [] as Array<{ id: string; property_id: string; maintenance_issue_id: string; title: string; start_at: string; end_at: string }>;
      const { data, error } = await supabase
        .from("vendor_visits")
        .select("id, property_id, maintenance_issue_id, title, start_at, end_at")
        .in("property_id", propertyIds)
        .order("start_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; property_id: string; maintenance_issue_id: string; title: string; start_at: string; end_at: string }>;
    },
    enabled: propertyIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  const allTasks = useMemo(
    () => (taskQueries.flatMap((q) => (q.data ?? []) as CleaningTaskWithRelations[])),
    [taskQueries]
  );
  const assigneeUserIds = useMemo(
    () => [...new Set(allTasks.map((t) => (t as { assignee_user_id?: string | null }).assignee_user_id).filter(Boolean))] as string[],
    [allTasks]
  );

  const profilesQuery = useQuery({
    queryKey: ["profiles-display", assigneeUserIds],
    queryFn: async () => {
      if (assigneeUserIds.length === 0) return [] as Array<{ id: string; full_name: string | null; email?: string | null }>;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", assigneeUserIds);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; full_name: string | null; email?: string | null }>;
    },
    enabled: assigneeUserIds.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  const ownerIdByPropertyId = useMemo(() => {
    const list = (propertiesQuery.data ?? []) as Array<{ id: string; owner_id?: string }>;
    return Object.fromEntries(list.map((p) => [p.id, p.owner_id ?? null]));
  }, [propertiesQuery.data]);

  const assigneeUserIdToName = useMemo(() => {
    const list = (profilesQuery.data ?? []) as Array<{ id: string; full_name?: string | null; email?: string | null }>;
    return new Map(
      list.map((p) => [p.id, (p.full_name ?? "").trim() || (p.email ?? "").split("@")[0] || "—"])
    );
  }, [profilesQuery.data]);

  const vendorVisits = (vendorVisitsQuery.data ?? []) as Array<{
    id: string;
    property_id: string;
    maintenance_issue_id: string;
    title: string;
    start_at: string;
    end_at: string;
  }>;

  const byPropertyId = useMemo(() => {
    const out: Record<string, ScheduleItem[]> = {};
    propertyIds.forEach((id, i) => {
      const reservations = (reservationQueries[i]?.data ?? []) as Array<Record<string, unknown>>;
      const tasks = (taskQueries[i]?.data ?? []) as CleaningTaskWithRelations[];
      const ownerId = ownerIdByPropertyId[id] ?? null;
      const items = buildItemsForProperty(reservations, tasks, ownerId, assigneeUserIdToName);
      const visitsForProperty = vendorVisits.filter((v) => v.property_id === id);
      visitsForProperty.forEach((v) => {
        items.push({
          id: `vendor-visit-${v.id}`,
          type: "vendor_visit",
          start: v.start_at,
          end: v.end_at,
          title: v.title,
          status: "scheduled",
          vendorVisitPayload: {
            id: v.id,
            maintenance_issue_id: v.maintenance_issue_id,
            title: v.title,
            start_at: v.start_at,
            end_at: v.end_at,
          },
        });
      });
      items.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      out[id] = items;
    });
    return out;
  }, [propertyIds, reservationQueries, taskQueries, ownerIdByPropertyId, assigneeUserIdToName, vendorVisits]);

  const isLoading =
    reservationQueries.some((q) => q.isLoading) ||
    taskQueries.some((q) => q.isLoading) ||
    vendorVisitsQuery.isLoading ||
    (propertyIds.length > 0 && propertiesQuery.isLoading);

  return { byPropertyId, isLoading };
}
